import {Col, Container, FormControl, Row} from 'react-bootstrap'
import css from '../../components/css/HoursForMsisdn.module.css'
import React, {useEffect, useState} from 'react';
import {getMsisdnsForCompanyId} from '../../API/msisdnApi';
import {toast} from 'react-toastify';
import messageFromError from '../../utils/errorUtils';
import MsisdnSelection from './MsisdnSelection';
import useCompanyId from '../../Hooks/useCompanyId';
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import NoElementsAlert from "../../components/NoElementsAlert";
import ConditionalRender, {Fallback} from "../../components/ConditionalRender";
import PaginationComponent from "../../components/core/Pagination/PaginationComponent";
import {useTranslation} from "react-i18next";
import Search from "../../components/core/Components/Search";
import WeekSelector from "../../components/WeekSelector";


export default function HoursForMsisdns({periodSelector, transactions, events, isExporting, isLoading, onMsisdnSelected, onWeekSelected, onExport})
{
    const [startDate, setStartDate] = useState(getDefaultStartDate());
    const [startDateInput, setStartDateInput] = useState(getDefaultStartDate());
    const [endDate, setEndDate] = useState(getDefaultEndDate());
    const [endDateInput, setEndDateInput] = useState(getDefaultEndDate());

    const [selectedWeek, setSelectedWeek] = useState(null);
    const [msisdns, setMsisdns] = useState()
    const [selectedMsisdn, setSelectedMsisdn] = useState(null)

    const companyId = useCompanyId()

    const {t} = useTranslation()

    const [page, setPage] = useState(0)
    const [search, setSearch] = useState('')

    useEffect(() => {
        if(periodSelector === 'week')
        {
            onWeekSelected && onWeekSelected(selectedWeek)
        }
        else if (periodSelector === 'range')
        {
            onWeekSelected && onWeekSelected({startDate, endDate})
        }
    }, [periodSelector]);


    useEffect(() => {
        fetchMsisdns()
    }, [page])

    useEffect(() => {
        fetchMsisdns(false)
    }, [search])

    useEffect(() => {
        onWeekSelected && onWeekSelected(selectedWeek)
    }, [selectedWeek]);

    useEffect(() => {
        const startDateUtc = new Date(startDateInput)
        startDateUtc.setUTCHours(0, 0, 0)
        setStartDate(startDateUtc)

        const endDateUtc = new Date(endDateInput)
        endDateUtc.setUTCHours(23, 59, 59)
        setEndDate(endDateUtc)

    }, [startDateInput, endDateInput])

    useEffect(() => {
        setSelectedWeek({startDate, endDate})
    }, [startDate, endDate]);

    async function fetchMsisdns()
    {
        try {
            const response = await getMsisdnsForCompanyId(companyId, page, search)
            setMsisdns(response.data)
        } catch (error) {
            toast.error(messageFromError(error))
        }
    }

    function formatDateReverse(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so add 1 and pad with 0 if necessary.
        const day = String(date.getDate()).padStart(2, '0');
      
        return `${year}-${month}-${day}`;
    }

    function getDefaultStartDate()
    {
        const date = new Date();
        const firstDayUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
        firstDayUTC.setUTCHours(0, 0, 0);
        return firstDayUTC;
    }

    function getDefaultEndDate()
    {
        return new Date();
    }

    const handleMsisdnsSelected = (msisdn) => {
        setSelectedMsisdn(msisdn)
        onMsisdnSelected && onMsisdnSelected(msisdn)
    }

    if(!msisdns)
        return <LoadingSpinner className='mt-4'/>;

    return (
        <>
            <div className={css.container}>
                <Container className={css.title}>
                    <Row className='align-items-center'>
                        <Col className={'d-flex align-items-center'}>
                            <h5 className={'me-3'}>{t('pages.hoursOverview.title')}</h5>
                        </Col>
                    </Row>
                </Container>
                <div className={css.body}>
                    <ConditionalRender condition={periodSelector === 'week'}>
                        <Fallback>
                            <Row className='px-5 justify-content-around mt-3'>
                                <Col md={4}>
                                    <div>
                                        <span className='ms-1'>{t('global.time.from')}</span>
                                        <FormControl
                                            type="date"
                                            max={formatDateReverse(new Date())}
                                            value={formatDateReverse(startDateInput)}
                                            onChange={(e) => {
                                                const newStartDate = new Date(e.target.value)
                                                setStartDateInput(newStartDate)
                                                // We have to set the start date before adjusting the end date (if needed) to avoid making two calls to the API.
                                                // If the end date is smaller than the start date, the 'HoursForMsisdns' won't make a call to the API.

                                                if(newStartDate > endDateInput)
                                                {
                                                    setEndDateInput(newStartDate)
                                                }
                                            }}/>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div>
                                        <span className='ms-1'>{t('global.time.to')}</span>
                                        <FormControl
                                            type="date"
                                            min={formatDateReverse(startDateInput)}
                                            value={formatDateReverse(endDateInput)}
                                            onChange={(e) => {
                                               setEndDateInput(new Date(e.target.value))
                                            }}/>
                                    </div>
                                </Col>
                            </Row>
                        </Fallback>

                        <Row>
                            <Col>
                                <WeekSelector onChange={(weekInfo) => {setSelectedWeek(weekInfo)}}/>
                            </Col>
                        </Row>
                    </ConditionalRender>
                    <Row className={'justify-content-center my-3'}>
                        <Col md={6} lg={4}>
                            <Search onSearch={(t) => setSearch(t)} debounceMs={1000}/>
                        </Col>
                    </Row>
                    <Row className='justify-content-center'>
                        <Col md={10}>
                            <ConditionalRender condition={msisdns && msisdns.data.length > 0}>
                                <MsisdnSelection msisdns={msisdns.data} onChange={handleMsisdnsSelected}/>
                            </ConditionalRender>
                            <NoElementsAlert show={msisdns && msisdns.data.length < 1} text={t('pages.hoursOverview.noWorkers')}/>
                        </Col>
                    </Row>
                    <ConditionalRender condition={msisdns.totalPages > 1}>
                        <Row>
                            <Col className={'d-flex justify-content-center my-3'}>
                                <PaginationComponent lastPage={msisdns.lastPage} onPageChanged={(page) => setPage(page)}/>
                            </Col>
                        </Row>
                    </ConditionalRender>
                    <Row className='justify-content-center pb-4'>
                        <Col md={10}>
                            {selectedWeek && selectedWeek.startDate && selectedWeek.endDate &&
                                <ConditionalRender condition={msisdns && msisdns.data.length > 0}>
                                    <ConditionalRender condition={!isLoading}>
                                        {/*<MsisdnHoursList selectedMsisdn={selectedMsisdn}*/}
                                        {/*                 transactions={transactions}*/}
                                        {/*                 events={events}/>*/}

                                        <Fallback>
                                            <LoadingSpinner small className={'mt-2'}/>
                                        </Fallback>
                                    </ConditionalRender>
                                </ConditionalRender>}

                        </Col>
                    </Row>
                </div>
            </div>
        </>
    )
}